import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import toast from 'react-hot-toast';

export const fetchParkings = createAsyncThunk('parking/fetchAll', async (params, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params || {}).toString();
    const res = await api.get(`/parking?${query}`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch parkings');
  }
});

export const fetchNearby = createAsyncThunk('parking/fetchNearby', async ({ lat, lng, radius }, { rejectWithValue }) => {
  try {
    const res = await api.get(`/parking/nearby?lat=${lat}&lng=${lng}&radius=${radius || 5}`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch nearby parkings');
  }
});

export const fetchParkingById = createAsyncThunk('parking/fetchById', async (id, { rejectWithValue }) => {
  try {
    const res = await api.get(`/parking/${id}`);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to fetch parking');
  }
});

export const addParking = createAsyncThunk('parking/add', async (data, { rejectWithValue }) => {
  try {
    const res = await api.post('/parking', data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to add parking');
  }
});

export const updateParking = createAsyncThunk('parking/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await api.put(`/parking/${id}`, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to update parking');
  }
});

export const deleteParking = createAsyncThunk('parking/delete', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/parking/${id}`);
    return id;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to delete parking');
  }
});

export const addReview = createAsyncThunk('parking/addReview', async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await api.post(`/parking/${id}/reviews`, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.message || 'Failed to add review');
  }
});

const parkingSlice = createSlice({
  name: 'parking',
  initialState: {
    parkings: [],
    nearby: [],
    selected: null,
    loading: false,
    error: null,
    total: 0,
    filters: { search: '', vehicleType: '', minPrice: '', maxPrice: '', amenities: [] },
  },
  reducers: {
    setFilters: (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    clearSelected: (state) => { state.selected = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchParkings.pending, (state) => { state.loading = true; })
      .addCase(fetchParkings.fulfilled, (state, action) => {
        state.loading = false;
        state.parkings = action.payload.data || [];
        state.total = action.payload.count || 0;
      })
      .addCase(fetchParkings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchNearby.fulfilled, (state, action) => {
        state.nearby = action.payload.data || [];
      })
      .addCase(fetchParkingById.pending, (state) => { state.loading = true; })
      .addCase(fetchParkingById.fulfilled, (state, action) => {
        state.loading = false;
        state.selected = action.payload.data;
      })
      .addCase(fetchParkingById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addParking.fulfilled, (state, action) => {
        state.parkings.unshift(action.payload.data);
        toast.success('Parking added successfully!');
      })
      .addCase(updateParking.fulfilled, (state, action) => {
        const idx = state.parkings.findIndex(p => p._id === action.payload.data._id);
        if (idx !== -1) state.parkings[idx] = action.payload.data;
        state.selected = action.payload.data;
        toast.success('Parking updated!');
      })
      .addCase(deleteParking.fulfilled, (state, action) => {
        state.parkings = state.parkings.filter(p => p._id !== action.payload);
        toast.success('Parking deleted!');
      })
      .addCase(addReview.fulfilled, (state, action) => {
        state.selected = action.payload.data;
        toast.success('Review added!');
      });
  },
});

export const { setFilters, clearSelected, clearError } = parkingSlice.actions;
export default parkingSlice.reducer;